import { NmLoader } from '../NmLoader';
import { SportDurationDonut } from '../charts/SportDurationDonut';
import type { WeekBar } from '../charts/types';
import { displayDigits } from '../../utils/displayDigits';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { calendar, type PmcDataPoint } from '../../services/api';
import { ChartPmcSplit } from '../charts/ChartPmcSplit';

/** Read-only performance history for the calendar's athlete, independent of saved dashboard charts. */
export function CalendarPerformanceChart({ userId, onClose, durationData }: { userId: number | null; onClose: () => void; durationData?: WeekBar[] }) {
  const { t, i18n } = useTranslation();
  const totalDuration = durationData?.reduce((sum, row) => sum + row.value, 0) ?? 0;
  const durationPercent = (value: number) => new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 }).format(totalDuration > 0 ? value / totalDuration : 0);
  const dialog = useRef<HTMLDialogElement>(null);
  const [data, setData] = useState<PmcDataPoint[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { dialog.current?.showModal(); }, []);
  useEffect(() => {
    if (durationData !== undefined || userId == null) return;
    let live = true;
    setData(null);
    setFailed(false);
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 89);
    const iso = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    void calendar.getCalendarData(userId, iso(start), iso(end)).then(response => {
      const rows = response.data.data.filter(day => day.today && Number(day.today.id) > 0)
        .map(day => ({ date: day.day.slice(0, 10), ctl: day.today!.ctl, atl: day.today!.atl, tsb: day.today!.tsb }));
      if (live) setData(rows);
    }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [userId, durationData]);
  return createPortal(<dialog ref={dialog} className="calendar-performance-chart" aria-labelledby="calendar-performance-title"
    onClose={onClose} onClick={event => { if (event.target === event.currentTarget) dialog.current?.close(); }}>
    <div className="calendar-performance-chart__content">
      <header>
        <h2 id="calendar-performance-title">{t(durationData !== undefined ? 'sportDuration.title' : 'charts.pmcSplit.title')}</h2>
        <button type="button" autoFocus onClick={() => dialog.current?.close()} aria-label={t('common.close')}>×</button>
      </header>
      {durationData !== undefined ? <><SportDurationDonut data={durationData} maximumFractionDigits={0} />
        <ul className="calendar-duration-breakdown">{durationData.map(row => <li key={row.label}><span>{t(`sportDuration.sports.${row.label}`)} <bdi className="calendar-duration-breakdown__percent">{durationPercent(row.value)}</bdi></span><bdi>{displayDigits(`${Math.floor(Math.round(row.value / 60) / 60)}:${String(Math.round(row.value / 60) % 60).padStart(2, '0')}`)}</bdi></li>)}</ul></> : failed ? <p role="alert">{t('toast.loadFailed')}</p> : data == null ? <p role="status"><NmLoader size="sm" inline label={t('charts.loading')} /></p> :
        data.length === 0 ? <p>{t('home.trend.noData')}</p> : <div className="ltr-island"><ChartPmcSplit data={{ pmc: data.map(row => ({ ...row, calculation_date: row.date })) }}
          settings={{ pmc: { show_pmc: true, show_CTL: true, show_ATL: true, show_form_TSB: true, show_TSS: false, show_IF: false } }} /></div>}
    </div>
  </dialog>, document.body);
}
